import {
  apiRequest,
  buildLoginFetchUrl,
  buildSsoConfigFetchUrl,
  buildSsoCallbackFetchUrl,
  fetchApi,
  getStoredAccessToken,
  readErrorMessage,
  setStoredAccessToken,
  setStoredApiBaseUrl,
} from "./client";
import { APP_NAME } from "../config/buildMeta";
import { isTokenExpired } from "../utils/jwt";
import type { AuthUser } from "../types/auth";

export type { AuthUser };

export interface LoginRequest {
  /** Apex base (origin or ``/apex``-style path); ``/api/auth/login`` is appended when sending. */
  loginUrl: string;
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  user?: Partial<AuthUser>;
}

export interface SsoConfig {
  ssoEnabled: boolean;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Connection: string;
}

export interface SsoCallbackRequest {
  loginUrl: string;
  idToken: string;
}

export interface SsoCallbackResponse {
  accessToken: string;
}

function extractToken(data: Record<string, unknown>): string {
  return (
    (data.access_token as string) ||
    (data.accessToken as string) ||
    (data.token as string) ||
    ""
  );
}

/** Backend may return any of these keys for the Contract Hub API origin. */
function extractBackendBaseUrl(data: Record<string, unknown>): string | null {
  const candidates = [
    data.backend_base_url,
    data.backendBaseUrl,
    data.base_url,
    data.baseUrl,
    data.api_base_url,
    data.apiBaseUrl,
    data.backend_url,
    data.backendUrl,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) {
      return c.trim();
    }
  }
  return null;
}

function mapMePayload(data: Record<string, unknown>): AuthUser {
  // Contract-hub ``GET /auth/me`` → ``UserProfile`` (user_id, name, email).
  const id = String(data.id ?? data.userId ?? data.user_id ?? "");
  const name = String(
    data.name ?? data.displayName ?? data.display_name ?? data.givenName ?? data.given_name ?? ""
  );
  const email = String(data.email ?? "");
  return { id, name, email };
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const raw = credentials.loginUrl.trim();
    if (!raw) {
      throw new Error("Login URL is required.");
    }

    let fetchUrl: string;
    try {
      fetchUrl = buildLoginFetchUrl(raw);
    } catch {
      throw new Error("Invalid login URL.");
    }

    let resp: Response;
    try {
      resp = await fetchApi(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          app_name: APP_NAME,
        }),
      });
    } catch {
      throw new Error("The login url is not responding. Please check the url");
    }

    if (!resp.ok) {
      throw new Error(await readErrorMessage(resp));
    }

    const data = (await resp.json()) as Record<string, unknown>;
    const accessToken = extractToken(data);
    if (!accessToken) {
      throw new Error("Login response did not include an access token.");
    }

    const backendBase = extractBackendBaseUrl(data);
    if (backendBase) {
      setStoredApiBaseUrl(backendBase);
    } else {
      setStoredApiBaseUrl(null);
    }

    setStoredAccessToken(accessToken);
    return { accessToken };
  },

  logout: async (): Promise<void> => {
    try {
      await apiRequest("/api/auth/logout", { method: "POST" });
    } catch {
      // still clear locally
    } finally {
      setStoredAccessToken(null);
      setStoredApiBaseUrl(null);
    }
  },

  me: async (): Promise<AuthUser> => {
    const data = await apiRequest<Record<string, unknown>>("/auth/me", { method: "GET" });
    return mapMePayload(data);
  },

  /** GET {loginUrl}/api/auth/sso/config → Auth0 domain, client ID, and whether SSO is enabled. */
  ssoConfig: async (loginUrl: string): Promise<SsoConfig> => {
    const raw = loginUrl.trim();
    if (!raw) throw new Error("Login URL is required.");

    let fetchUrl: string;
    try {
      fetchUrl = buildSsoConfigFetchUrl(raw);
    } catch {
      throw new Error("Invalid login URL.");
    }

    let resp: Response;
    try {
      resp = await fetchApi(fetchUrl, { method: "GET", headers: { Accept: "application/json" } });
    } catch {
      throw new Error("The login URL is not responding. Please check the URL.");
    }

    if (!resp.ok) {
      throw new Error(await readErrorMessage(resp));
    }

    const data = (await resp.json()) as Record<string, unknown>;
    const ssoEnabled = Boolean(data.enabled ?? data.sso_enabled ?? data.ssoEnabled ?? false);
    const auth0Domain = String(data.auth0_domain ?? data.auth0Domain ?? "");
    const auth0ClientId = String(data.auth0_client_id ?? data.auth0ClientId ?? "");
    const auth0Connection = String(data.auth0_connection ?? data.auth0Connection ?? "");

    if (ssoEnabled && (!auth0Domain || !auth0ClientId)) {
      throw new Error("SSO is enabled but Auth0 configuration is incomplete.");
    }

    return { ssoEnabled, auth0Domain, auth0ClientId, auth0Connection };
  },

  /** POST {loginUrl}/api/auth/sso/callback with the Auth0 id_token → Apex access token. */
  ssoCallback: async (req: SsoCallbackRequest): Promise<SsoCallbackResponse> => {
    const raw = req.loginUrl.trim();
    if (!raw) throw new Error("Login URL is required.");

    let fetchUrl: string;
    try {
      fetchUrl = buildSsoCallbackFetchUrl(raw);
    } catch {
      throw new Error("Invalid login URL.");
    }

    let resp: Response;
    try {
      resp = await fetchApi(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: req.idToken, app_name: APP_NAME }),
      });
    } catch {
      throw new Error("SSO callback failed. The server is not responding.");
    }

    if (!resp.ok) {
      throw new Error(await readErrorMessage(resp));
    }

    const data = (await resp.json()) as Record<string, unknown>;
    const accessToken = extractToken(data);
    if (!accessToken) {
      throw new Error("SSO callback did not return an access token.");
    }

    const backendBase = extractBackendBaseUrl(data);
    if (backendBase) {
      setStoredApiBaseUrl(backendBase);
    } else {
      // No explicit backend URL from the response — use the login URL
      // so all subsequent API calls (GET /auth/me, /contracts, etc.) route there.
      const loginBase = raw.replace(/\/+$/, "");
      setStoredApiBaseUrl(loginBase || null);
    }

    setStoredAccessToken(accessToken);
    return { accessToken };
  },

  isAuthenticated: (): boolean => {
    const token = getStoredAccessToken();
    if (!token) {
      return false;
    }
    if (isTokenExpired(token)) {
      setStoredAccessToken(null);
      setStoredApiBaseUrl(null);
      return false;
    }
    return true;
  },
};
